/**
 * Settings → Sources & API keys (design-reference §4.9).
 *
 * Three stacked cards: the copyable ingest endpoints with an example payload, the
 * live ingestion sources (GET /v1/ingestion-sources), and the API keys
 * (GET /v1/api-keys) with create (reveal-once), rotate and revoke. Every value in
 * the two tables comes from a real API field; the endpoints card documents the
 * ingestion contract.
 */
import { useCallback, useState } from 'react';

import type { ApiKeyView, IngestionChannel, IngestionSourceView, IssuedApiKeyView } from '../../api/schema';
import { Button, ConfirmModal, EmptyState, ErrorState, Modal, Skeleton, useToast } from '../../components';
import { API_BASE_URL, MCP_BASE_URL } from '../../api/config';
import { useLoadState } from '../../lib/useLoadState';
import { useSettingsApi } from './settingsApi';
import { fmtDate, reasonOf, relativeAge } from './format';
import './settings.css';

// Exhaustive by type: adding a channel to the backend enum and to `schema.ts`
// fails the build here until it has a human-readable name, rather than rendering
// `undefined` in the table.
const CHANNEL_LABEL: Record<IngestionChannel, string> = {
  api: 'API',
  mcp: 'MCP',
  ui: 'Web upload',
  webhook: 'Webhook',
  email: 'Mailbox',
};

interface EndpointRow {
  key: string;
  value: string;
  hint: string;
}

// Built from the deployment's own configuration, never a literal: these rows
// carry a Copy button, so a baked-in host hands the integrator a URL that
// resolves nowhere. `{source}` stays a placeholder — it is the source's slug,
// which the integrator substitutes per source (see the Sources table below).
const ENDPOINTS: readonly EndpointRow[] = [
  {
    key: 'REST ingest',
    value: `POST ${API_BASE_URL}/v1/documents`,
    hint: 'multipart or {blob_uri} · X-API-Key · Idempotency-Key supported',
  },
  {
    key: 'Webhook',
    value: `POST ${API_BASE_URL}/v1/webhooks/ingest/{source}`,
    hint: 'HMAC-signed · per-source secret',
  },
  {
    key: 'MCP server',
    value: `${MCP_BASE_URL} (submit_document, query_volume, …)`,
    hint: 'tenant-scoped tokens',
  },
];

const EXAMPLE_PAYLOAD = `POST /v1/documents
Idempotency-Key: 9f3a-...
Content-Type: multipart/form-data

file=@invoice.pdf
source=branch-middleware
scanner_id=MFP-004`;

// Exactly the scopes the backend will grant to an api key (ApiKeyService's
// grantable set). Offering one it refuses — `analytics:read` used to be here —
// turns Create into a 422 the user cannot diagnose from the UI.
const SCOPE_OPTIONS = [
  'documents:write',
  'documents:read',
  'job_status:read',
  // Let a capture device register itself and read back the scanner list, so the
  // ids it quotes on every submission never have to be copied in by hand.
  'registries:write',
  'registries:read',
] as const;

interface SourcesData {
  sources: IngestionSourceView[];
  keys: ApiKeyView[];
}

function keyStatusActive(status: ApiKeyView['status']): boolean {
  return status === 'active';
}

export function SourcesScreen(): React.JSX.Element {
  const { toast } = useToast();
  const settingsApi = useSettingsApi();

  // Create-key flow.
  const [createOpen, setCreateOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [scopes, setScopes] = useState<Set<string>>(new Set(['documents:write']));
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<IssuedApiKeyView | null>(null);

  // Revoke flow.
  const [revoking, setRevoking] = useState<ApiKeyView | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  const fetchSources = useCallback(
    async (signal: AbortSignal): Promise<SourcesData> => {
      const [sources, keys] = await Promise.all([
        settingsApi.sources.list(signal),
        settingsApi.apiKeys.list(signal),
      ]);
      return { sources: sources.items, keys: keys.items };
    },
    [settingsApi],
  );
  const state = useLoadState(fetchSources);
  const reload = state.status === 'ready' || state.status === 'error' ? state.reload : undefined;

  const copy = useCallback(
    (text: string) => {
      const nav = navigator as { clipboard?: Clipboard };
      if (nav.clipboard) void nav.clipboard.writeText(text).catch(() => undefined);
      toast('Copied to clipboard');
    },
    [toast],
  );

  const openCreate = useCallback(() => {
    setKeyName('');
    setScopes(new Set(['documents:write']));
    setCreateOpen(true);
  }, []);

  const submitCreate = useCallback(() => {
    if (keyName.trim() === '') return;
    setCreating(true);
    void (async () => {
      try {
        const issued = await settingsApi.apiKeys.create({ name: keyName.trim(), scopes: [...scopes] });
        setCreateOpen(false);
        setRevealed(issued);
        toast('API key created');
        reload?.();
      } catch (error) {
        toast(reasonOf(error, 'Could not create the key — try again'), { tone: 'error' });
      } finally {
        setCreating(false);
      }
    })();
  }, [keyName, scopes, toast, reload, settingsApi]);

  const rotate = useCallback(
    (key: ApiKeyView) => {
      void (async () => {
        try {
          const issued = await settingsApi.apiKeys.rotate(key.id);
          setRevealed(issued);
          toast('Key rotated — update your integration within 24 h (old key grace period)');
          reload?.();
        } catch {
          toast('Could not rotate the key — try again', { tone: 'error' });
        }
      })();
    },
    [toast, reload, settingsApi],
  );

  const confirmRevoke = useCallback(() => {
    const key = revoking;
    if (key === null) return;
    setRevokeBusy(true);
    void (async () => {
      try {
        await settingsApi.apiKeys.revoke(key.id);
        toast('Key revoked — requests with it now return 401');
        setRevoking(null);
        reload?.();
      } catch {
        toast('Could not revoke the key — try again', { tone: 'error' });
      } finally {
        setRevokeBusy(false);
      }
    })();
  }, [revoking, toast, reload, settingsApi]);

  if (state.status === 'loading') {
    return (
      <section className="set-wrap" aria-label="Sources & API keys">
        <Skeleton height={120} />
        <Skeleton height={160} />
        <Skeleton height={160} />
        <p className="set-loading-caption">Loading sources and keys…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="set-wrap" aria-label="Sources & API keys">
        <ErrorState
          title="We couldn't load your sources"
          body="The settings service didn't respond. Your ingestion keeps running — this only affects this view."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const { sources, keys } = state.data;

  return (
    <section className="set-wrap" aria-label="Sources & API keys">
      {/* (a) Ingest endpoints ------------------------------------------------ */}
      <div className="set-card">
        <h2 className="set-card__title">Ingest endpoints</h2>
        {ENDPOINTS.map((row) => (
          <div className="set-endpoint" key={row.key}>
            <span className="set-endpoint__key">{row.key}</span>
            <span className="set-endpoint__val" title={row.value}>
              {row.value}
            </span>
            <span className="set-endpoint__hint">{row.hint}</span>
            <button type="button" className="set-copybtn" onClick={() => { copy(row.value); }}>
              Copy
            </button>
          </div>
        ))}
        <details>
          <summary className="set-hint" style={{ cursor: 'pointer' }}>
            Example payload
          </summary>
          <pre className="set-mono" style={{ fontSize: 11, color: 'var(--t2)', overflowX: 'auto', margin: '8px 0 0' }}>
            {EXAMPLE_PAYLOAD}
          </pre>
        </details>
      </div>

      {/* (b) Sources --------------------------------------------------------- */}
      <div className="set-card">
        <div className="set-card__head">
          <h2 className="set-card__title">Sources</h2>
          <span className="set-card__aside set-card__spacer">how documents reach Docsight</span>
        </div>
        {sources.length === 0 ? (
          <EmptyState
            title="Connect your first source"
            body="No ingestion sources yet. Point a scanner at POST /v1/documents, register a webhook bridge, or upload from the dashboard to see traffic here."
            actions={
              <Button variant="primary" onClick={openCreate}>
                Create an API key
              </Button>
            }
          />
        ) : (
          <div className="set-scroll">
            <div
              className="set-grid set-grid__head"
              role="row"
              style={{ gridTemplateColumns: '1fr 1.6fr 1fr 1fr' }}
            >
              <span role="columnheader">Channel</span>
              <span role="columnheader">Name</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Last seen</span>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Ingestion sources">
              {sources.map((s) => (
                <li
                  className="set-grid set-grid__row"
                  key={s.id}
                  style={{ gridTemplateColumns: '1fr 1.6fr 1fr 1fr' }}
                >
                  <span>{CHANNEL_LABEL[s.channel]}</span>
                  <span>{s.name}</span>
                  <span className="set-status">
                    <span
                      className={`set-dot ${s.status === 'active' ? 'set-dot--ok' : 'set-dot--idle'}`}
                      aria-hidden="true"
                    />
                    {s.status === 'active' ? 'Active' : s.status === 'suspended' ? 'Suspended' : 'Archived'}
                  </span>
                  <span className="set-mono" style={{ color: 'var(--t3)' }}>
                    {relativeAge(s.last_seen_at)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* (c) API keys -------------------------------------------------------- */}
      <div className="set-card">
        <div className="set-card__head">
          <h2 className="set-card__title">API keys</h2>
          <span className="set-card__spacer" />
          <Button variant="primary" size="sm" onClick={openCreate}>
            + Create key
          </Button>
        </div>
        {keys.length === 0 ? (
          <p className="set-hint">No keys yet. Create one to let a scanner or integration post documents.</p>
        ) : (
          <div className="set-scroll">
            <div
              className="set-grid set-grid__head"
              role="row"
              style={{ gridTemplateColumns: '1.3fr 1.3fr 1.4fr 0.9fr 0.9fr 0.8fr 150px' }}
            >
              <span role="columnheader">Name</span>
              <span role="columnheader">Key</span>
              <span role="columnheader">Scopes</span>
              <span role="columnheader">Created</span>
              <span role="columnheader">Last used</span>
              <span role="columnheader">Status</span>
              <span role="columnheader" className="sr-only">
                Actions
              </span>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="API keys">
              {keys.map((k) => {
                const active = keyStatusActive(k.status);
                return (
                  <li
                    className="set-grid set-grid__row"
                    key={k.id}
                    style={{ gridTemplateColumns: '1.3fr 1.3fr 1.4fr 0.9fr 0.9fr 0.8fr 150px' }}
                  >
                    <span style={{ fontWeight: 500 }}>{k.name}</span>
                    <span className="set-mono" style={{ fontSize: 11, color: 'var(--t2)' }}>
                      {k.key_prefix ?? '—'}
                    </span>
                    <span className="set-mono" style={{ fontSize: 10.5, color: 'var(--t2)' }}>
                      {k.scopes.join(', ')}
                    </span>
                    <span style={{ color: 'var(--t3)' }}>{fmtDate(k.created_at)}</span>
                    <span style={{ color: 'var(--t3)' }}>{relativeAge(k.last_used_at)}</span>
                    <span className="set-status">
                      <span
                        className={`set-dot ${active ? 'set-dot--ok' : 'set-dot--bad'}`}
                        aria-hidden="true"
                      />
                      {active ? 'Active' : 'Revoked'}
                    </span>
                    <span className="set-rowactions">
                      {active ? (
                        <>
                          <button type="button" className="set-linkbtn" onClick={() => { rotate(k); }}>
                            Rotate
                          </button>
                          <button
                            type="button"
                            className="set-linkbtn set-linkbtn--bad"
                            onClick={() => { setRevoking(k); }}
                          >
                            Revoke
                          </button>
                        </>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <p className="set-card__foot">
          Keys are hashed at rest — the secret is shown once at creation. Machine callers can also use OAuth2
          client-credentials.
        </p>
      </div>

      {/* Create-key modal — step 1 ------------------------------------------ */}
      <Modal
        open={createOpen}
        onClose={() => { setCreateOpen(false); }}
        title="Create API key"
        dismissible
        footer={
          <div className="ds-modal__footer">
            <Button variant="secondary" onClick={() => { setCreateOpen(false); }}>
              Cancel
            </Button>
            <Button variant="primary" loading={creating} disabled={keyName.trim() === ''} onClick={submitCreate}>
              Create key
            </Button>
          </div>
        }
      >
        <div className="ds-modal__body">
          <p className="set-hint" style={{ marginTop: 0 }}>
            Scoped, hashed at rest, revocable any time.
          </p>
          <div className="ds-field">
            <label className="ds-field__label" htmlFor="key-name">
              Name
            </label>
            <input
              id="key-name"
              className="ds-input"
              placeholder="e.g. Warehouse scanners"
              value={keyName}
              onChange={(e) => { setKeyName(e.currentTarget.value); }}
            />
          </div>
          <fieldset style={{ border: 'none', margin: '12px 0 0', padding: 0 }}>
            <legend className="ds-field__label">Scopes</legend>
            {SCOPE_OPTIONS.map((scope) => (
              <label key={scope} className="ds-check" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={scopes.has(scope)}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    setScopes((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(scope);
                      else next.delete(scope);
                      return next;
                    });
                  }}
                />
                <span className="set-mono" style={{ fontSize: 11.5 }}>
                  {scope}
                </span>
              </label>
            ))}
          </fieldset>
        </div>
      </Modal>

      {/* Reveal modal — step 2 (create + rotate share it) ------------------- */}
      <Modal
        open={revealed !== null}
        onClose={() => { setRevealed(null); }}
        title="Your new key — copy it now"
        footer={
          <div className="ds-modal__footer">
            <Button variant="primary" onClick={() => { setRevealed(null); }}>
              I&apos;ve stored it safely
            </Button>
          </div>
        }
      >
        <div className="ds-modal__body">
          <p style={{ marginTop: 0 }}>
            This is the <strong style={{ color: 'var(--warn)' }}>only time</strong> the full secret is shown. We
            store a hash.
          </p>
          {revealed !== null ? (
            <div
              style={{
                background: 'var(--t1)',
                borderRadius: 10,
                padding: '12px 14px',
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <code
                className="set-mono"
                style={{ fontSize: 12, color: 'var(--bg)', wordBreak: 'break-all', flex: 1 }}
                data-testid="revealed-secret"
              >
                {revealed.raw_key}
              </code>
              <Button variant="primary" size="sm" onClick={() => { copy(revealed.raw_key); }}>
                Copy
              </Button>
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Revoke confirm ----------------------------------------------------- */}
      <ConfirmModal
        open={revoking !== null}
        onCancel={() => { setRevoking(null); }}
        onConfirm={confirmRevoke}
        title={`Revoke "${revoking?.name ?? ''}"?`}
        destructive
        confirmLabel="Revoke key"
        cancelLabel="Keep it"
        loading={revokeBusy}
      >
        Requests using this key start failing with <code className="set-mono">401</code> immediately. Any scanner
        or integration using it will stop ingesting. This can&apos;t be undone.
      </ConfirmModal>
    </section>
  );
}
